import { useEffect, useRef, type ReactNode } from 'react'
import { ScrollTrigger, registerMotion } from './gsap'
import { useReveal } from './useReveal'
import { useSplitLines } from './useSplitLines'
import { useDrawPath } from './useDrawPath'
import { useRouteTransition } from './useRouteTransition'

/**
 * The motion layer of one routed page: a wrapper `div` that scopes every hook
 * below (selectors never reach the header, the footer or another page).
 * SiteRoutes keys it by pathname, so each navigation unmounts the old page's
 * layer — useGSAP reverts its tweens, ScrollTriggers and splits — and mounts
 * a fresh one.
 *
 * Spec §6: route transition, line splits, reveals and the zone map's water
 * only — no hero intro, services crossfade, pinned scenes or counters on this site
 * (the hero, Chi siamo, Numeri and the carousel keep their own CSS /
 * IntersectionObserver motion).
 *
 * Every hook is a no-op when its targets are absent and creates GSAP objects
 * only inside useGSAP (a layout effect: never during render, never on the
 * server). The prerendered markup is the final, fully visible state.
 */
export function PageMotion({ children, animateIn = false }: { children: ReactNode; animateIn?: boolean }) {
  const scope = useRef<HTMLDivElement>(null)
  useRouteTransition(scope, animateIn)
  useSplitLines(scope) // top of page first: ScrollTriggers are created in page order
  useReveal(scope)
  useDrawPath(scope)
  useEffect(() => {
    // Trigger positions depend on the layout above them: refresh once the
    // page's eager images have decoded and the web fonts are ready. Lazy
    // images are left out: Picture reserves their box, and one inside a
    // display:none box never loads, so its decode() would never settle and
    // the refresh would never run.
    const imgs = Array.from(scope.current?.querySelectorAll('img') ?? []).filter(
      (i) => !i.complete && i.loading !== 'lazy',
    )
    let alive = true
    Promise.all([...imgs.map((i) => i.decode().catch(() => undefined)), document.fonts?.ready]).then(() => {
      if (!alive) return
      registerMotion()
      ScrollTrigger.refresh()
    })
    return () => {
      alive = false
    }
  }, [])
  return (
    <div ref={scope} data-page-motion>
      {children}
    </div>
  )
}
